import type { Frame, Light } from './renderer/renderer'

/** The shorter side below which the window is a phone, and gets fewer particles. */
const PHONE_SIDE = 600

/** The window's width below which the page uses its single-column layout. */
export const MOBILE_WIDTH = 880

/** The page's size and sharpness at one moment, as values. Measured again on resize, never edited. */
export interface Viewport {
  /** CSS pixels. */
  readonly width: number
  readonly height: number
  /** The centre of the window. */
  readonly cx: number
  readonly cy: number
  /** The shorter side: the unit that lengths on the canvas are drawn in. */
  readonly min: number
  /** Device pixels per CSS pixel, capped at `maxDpr`. */
  readonly dpr: number
  /** True when the window is narrow enough for the single-column layout. */
  readonly mobile: boolean
}

/** Reads the window. `maxDpr` caps the canvas's sharpness (phones are 3, laptops 2). */
export function measureViewport(maxDpr: number): Viewport {
  const width = window.innerWidth
  const height = window.innerHeight
  return {
    width,
    height,
    cx: width / 2,
    cy: height / 2,
    min: Math.min(width, height),
    dpr: Math.min(window.devicePixelRatio || 1, maxDpr),
    mobile: width < MOBILE_WIDTH,
  }
}

/** What a frame is drawn at: the viewport and the backlight, as the renderer takes them. */
export function frameOf(viewport: Viewport, light: Light): Frame {
  return { width: viewport.width, height: viewport.height, dpr: viewport.dpr, light }
}

/** True on a phone: the shorter side of the window is small. */
export function isPhone(): boolean {
  return Math.min(window.innerWidth, window.innerHeight) < PHONE_SIDE
}
